/**
 * DebugPanel — mirrors ConneXt v2 DebugPanel.
 * Fixed-position LEFT-side panel (matching ConneXt v2 template style="left:0").
 * Uses the SAME class/id names as ConneXt v2 so the external plugin.debug.css styles it.
 */
import { View } from '@Engage.Data/modules/view/View';
import { UserStore } from '../../auth/model/UserStore';
import { localStorageService } from '@Engage.Data/services/storage/localStorage/LocalStorageService';
import { StorageKeys } from '@Engage.Data.Constants';
import { getVersionInfo, customTime, logService } from '@Engage.Core';
import { AppOptions } from '../../../application/options/AppOptions';
import { GlobalLocation } from '../../../application/GeoLocation';
import { DebugHelper } from './DebugHelper';
import { DebugPanelRepository } from './DebugPanelRepository';
class DebugPanelClass {
    constructor() {
        this.panelElementId = 'ConnextPanelDebugger';
        this.lastPaywallResponse = null;
    }
    /** Called during SDK init — shows panel if debug mode is on */
    init() {
        if (DebugPanelRepository.getDebugMode()) {
            this.bindData();
        }
    }
    /** Toggle debug mode on/off (reloads page) */
    toggle() {
        const current = DebugPanelRepository.getDebugMode();
        DebugPanelRepository.setDebugMode(!current);
        window.location.reload();
    }
    /** Store the paywall response for refreshData to pull campaign/meter info */
    setPaywallResponse(response) {
        this.lastPaywallResponse = response;
    }
    bindData() {
        this.loadStyles();
        const html = this.getHtmlCode();
        this.addToDocument(html);
        this.registerEvents();
        this.checkOpenState();
    }
    /** Refresh displayed values (called after paywall response) */
    refreshData() {
        if (!DebugHelper.elById(this.panelElementId))
            return;
        const data = this.getDataForPanel();
        this.setInputValue('ddMeterLevel', data.meterLevel);
        this.setInputValue('ddCampaign', data.campaignName);
        this.setInputValue('ddCurrentConversationArticleViews', data.conversationViewCount);
        this.setInputValue('ddDynamicMeterArticleViews', data.dynamicMeterArticleViews);
        this.setInputValue('engageConversationName', data.conversationName);
        this.setInputValue('engageUserState', data.userState);
        this.setInputValue('engageConvViewsLimit', data.conversationViewsLimit);
        this.setInputValue('engageConvViewsLeft', data.conversationViewsLeft);
        this.setInputValue('engageMeterViewsLimit', data.meterViewsLimit);
        this.setInputValue('engageMeterViewsLeft', data.meterViewsLeft);
        this.setInputValue('ddIpAccess', data.ipAccess);
    }
    getDataForPanel() {
        var _a, _b;
        const stored = localStorageService.get(StorageKeys.CurrentConversation, { expandKey: false });
        const startDate = View.getConversationStartDate();
        const resp = this.lastPaywallResponse;
        return {
            meterLevel: DebugHelper.wrapValueIfEmpty((_a = View.activeMeterView) === null || _a === void 0 ? void 0 : _a.meterLevel),
            campaignName: DebugHelper.wrapValueIfEmpty(this.getCampaignName()),
            conversationName: DebugHelper.wrapValueIfEmpty((stored === null || stored === void 0 ? void 0 : stored.Name) || (stored === null || stored === void 0 ? void 0 : stored.Code) || (resp === null || resp === void 0 ? void 0 : resp.conversationName)),
            conversationViewCount: View.getCurrentConversationViewCount() || 0,
            dynamicMeterArticleViews: View.getCurrentDynamicMeterViewCount() || 0,
            conversationViewsLimit: (resp === null || resp === void 0 ? void 0 : resp.conversationViewsLimit) != null ? resp.conversationViewsLimit : '...',
            conversationViewsLeft: (resp === null || resp === void 0 ? void 0 : resp.conversationViewsLeft) != null ? resp.conversationViewsLeft : '...',
            meterViewsLimit: (resp === null || resp === void 0 ? void 0 : resp.meterViewsLimit) != null ? resp.meterViewsLimit : '...',
            meterViewsLeft: (resp === null || resp === void 0 ? void 0 : resp.meterViewsLeft) != null ? resp.meterViewsLeft : '...',
            userState: ((_b = UserStore.getUser()) === null || _b === void 0 ? void 0 : _b.UserState) || 'LoggedOut',
            startDate: startDate ? customTime.formatDate(startDate) : '...',
            actualZipCodes: this.getZipCodes(),
            logLevels: this.getLogLevelOptions(),
            versionInfo: getVersionInfo(),
            engagementRate: DebugPanelRepository.getEngagementRate() || '1',
            ipAccess: 'Allowed'
        };
    }
    getCampaignName() {
        var _a, _b, _c, _d, _e, _f;
        // Try from cached paywall response first, then from window.Connext.Storage
        if ((_a = this.lastPaywallResponse) === null || _a === void 0 ? void 0 : _a.campaignName)
            return this.lastPaywallResponse.campaignName;
        if ((_b = this.lastPaywallResponse) === null || _b === void 0 ? void 0 : _b.CampaignName)
            return this.lastPaywallResponse.CampaignName;
        try {
            return ((_f = (_e = (_d = (_c = window.Connext) === null || _c === void 0 ? void 0 : _c.Storage) === null || _d === void 0 ? void 0 : _d.GetCurrentConversation) === null || _e === void 0 ? void 0 : _e.call(_d)) === null || _f === void 0 ? void 0 : _f.CampaignName) || '';
        }
        catch (_g) {
            return '';
        }
    }
    getZipCodes() {
        // 1. Debug override
        const stored = DebugPanelRepository.getZipCodes();
        if (stored)
            return stored;
        // 2. User profile zip codes
        const user = UserStore.getUser();
        if ((user === null || user === void 0 ? void 0 : user.ZipCodes) && user.ZipCodes.length > 0) {
            return user.ZipCodes.join(', ');
        }
        // 3. Geo location zip (mirrors ConneXt v2 User.getUserZipCodes -> GlobalLocation fallback)
        return GlobalLocation.getZipCode() || '';
    }
    getHtmlCode() {
        const data = this.getDataForPanel();
        const config = this.getCustomConfigurationFromStorage();
        return `<div id="${this.panelElementId}" class="debug_details" style="left:0;">
            <div class="debug_details_icon">&nbsp;</div>
            <div class="debug_details_content">
                <h4>Engage Debug Panel</h4>
                <br />
                <div class="engageDebugPanel">
                    <table>
                        <tr class="debug_details_header hide_on_mobile">
                            <td width="50%"></td>
                            <td><a href="#" class="more highlight" id="connextClearAllData">Clear Paywall's Storage</a></td>
                        </tr>
                        <tr><td colspan="2"><p class="engageHeading"><b>Engage Configuration</b></p></td></tr>
                        <tr class="debug_details_header hide_on_mobile"><td><label>Version</label></td><td><input id="connextVersion" class="formControl" type="text" value="${data.versionInfo}" disabled /></td></tr>
                        <tr class="debug_details_header hide_on_mobile">
                            <td><label class="overlay_label check my5" for="ConnextCustomConfiguration">Custom Configuration</label></td>
                            <td class="text-right"><label class="connextSwitch"><input type="checkbox" id="ConnextCustomConfiguration" ${config.isCustomConfiguration ? 'checked' : ''}><span class="connextSlider round"></span></label></td>
                        </tr>
                    </table>
                    <table id="ConnextCustomConfigurationDiv" style="display:${config.isCustomConfiguration ? '' : 'none'};">
                        <tr class="debug_details_header hide_on_mobile"><td width="50%"><label for="ConnextSiteCode">Site Code</label></td><td><input type="text" id="ConnextSiteCode" class="formControl" value="${config.siteCode || ''}"></td></tr>
                        <tr class="debug_details_header hide_on_mobile"><td><label for="ConnextConfigCode">Config Code</label></td><td><input type="text" id="ConnextConfigCode" class="formControl" value="${config.configCode || ''}"></td></tr>
                        <tr class="debug_details_header hide_on_mobile"><td width="50%"></td><td><a href="#" class="more highlight my5" id="ConnextSetCustomConfiguration">Set configuration</a></td></tr>
                    </table>
                    <table id="connextDebugLastTable">
                        <tr class="debug_details_header hide_on_mobile"><td width="50%"><label>Meter Level</label></td><td><input id="ddMeterLevel" class="formControl" type="text" value="${data.meterLevel}" disabled /></td></tr>
                        <tr class="debug_details_header hide_on_mobile"><td><label>Campaign</label></td><td><input id="ddCampaign" class="formControl" type="text" value="${data.campaignName}" disabled /></td></tr>
                        <tr><td colspan="2"><p class="engageHeading"><b>Paywall Setup</b></p></td></tr>
                        <tr class="debug_details_header"><td><label class="overlay_label check">Conversation</label></td><td><input id="engageConversationName" class="formControl" type="text" value="${data.conversationName}" disabled /></td></tr>
                        <tr class="debug_details_header debug_er">
                            <td><label class="overlay_label check">Conversation Views</label></td>
                            <td><input type="number" class="formControl inline" id="ddCurrentConversationArticleViews" min="0" step="1" value="${data.conversationViewCount}"><a href="#" class="more highlight inlineBtn" id="ConnextCurrentConversationArticleViewsApplyBtn">Set</a></td>
                        </tr>
                        <tr class="debug_details_header hide_on_mobile"><td><label>Conversation Views Limit</label></td><td><input id="engageConvViewsLimit" class="formControl" type="text" value="${data.conversationViewsLimit}" disabled /></td></tr>
                        <tr class="debug_details_header hide_on_mobile"><td><label>Conversation Views Left</label></td><td><input id="engageConvViewsLeft" class="formControl" type="text" value="${data.conversationViewsLeft}" disabled /></td></tr>
                        <tr class="debug_details_header debug_er">
                            <td><label class="overlay_label check">Meter Views</label></td>
                            <td><input type="number" class="formControl inline" id="ddDynamicMeterArticleViews" min="0" step="1" value="${data.dynamicMeterArticleViews}"><a href="#" class="more highlight inlineBtn" id="ConnextDynamicMeterArticleViewsBtn">Set</a></td>
                        </tr>
                        <tr class="debug_details_header hide_on_mobile"><td><label>Meter Views Limit</label></td><td><input id="engageMeterViewsLimit" class="formControl" type="text" value="${data.meterViewsLimit}" disabled /></td></tr>
                        <tr class="debug_details_header hide_on_mobile"><td><label>Meter Views Left</label></td><td><input id="engageMeterViewsLeft" class="formControl" type="text" value="${data.meterViewsLeft}" disabled /></td></tr>
                        <tr class="debug_details_header hide_on_mobile">
                            <td><label class="overlay_label check">Zip Code</label></td>
                            <td><input type="text" class="formControl inline" id="actualZipCodesValue" value="${data.actualZipCodes}"><a href="#" class="more highlight inlineBtn" id="actualZipCodes">Set</a></td>
                        </tr>
                        <tr class="debug_details_header debug_er">
                            <td><label class="overlay_label check">Engagement Rate</label></td>
                            <td><input type="number" class="formControl inline" id="ConnextEngagementRate" min="0" max="1" step="0.01" value="${data.engagementRate}"><a href="#" class="more highlight inlineBtn" id="ConnextEngagementRateApplyBtn">Set</a></td>
                        </tr>
                        <tr class="debug_details_header debug_er">
                            <td><label class="overlay_label check">Local Date & Time</label></td>
                            <td><input type="text" id="ConnextCustomTimeTxt" placeholder="MM/DD/YYYY" value="" class="text_input hint formControl inline"><a href="#" class="more highlight inlineBtn" id="ConnextSetCustomTimeBtn">Set</a></td>
                        </tr>
                        <tr class="debug_details_header"><td><label class="overlay_label check">Log Level</label></td><td><select id="ConnextLogLevel" class="formControl">${data.logLevels}</select></td></tr>
                        <tr class="debug_details_header hide_on_mobile">
                            <td><label class="my5">IP Access</label></td>
                            <td><input class="formControl" type="text" id="ddIpAccess" value="${data.ipAccess}" disabled /></td>
                        </tr>
                        <tr class="debug_details_header hide_on_mobile">
                            <td><label class="overlay_label check my5" for="ConnextConfigurationIsActive">Configuration is Active</label></td>
                            <td class="text-right"><label class="connextSwitch"><input type="checkbox" id="ConnextConfigurationIsActive" checked><span class="connextSlider round"></span></label></td>
                        </tr>
                        <tr class="debug_details_header hide_on_mobile">
                            <td><label class="overlay_label check my5" for="ConnextDebugPanelOpenState">Debug Panel Open State</label></td>
                            <td class="text-right"><label class="connextSwitch"><input type="checkbox" id="ConnextDebugPanelOpenState"><span class="connextSlider round"></span></label></td>
                        </tr>
                    </table>
                </div>
            </div>
        </div>`;
    }
    getLogLevelOptions() {
        const current = DebugPanelRepository.getLogLevel();
        const levels = { 'Low': '0', 'Medium': '1', 'High': '2' };
        let options = '';
        for (const [name, val] of Object.entries(levels)) {
            options += DebugHelper.generateHtmlOptionElement(val, name, current === val);
        }
        return options;
    }
    getCustomConfigurationFromStorage() {
        try {
            const rawCustom = localStorage.getItem('Connext_Configuration_isCustom');
            const rawSite = localStorage.getItem('Connext_Configuration_SiteCode');
            const rawConfig = localStorage.getItem('Connext_Configuration_ConfigCode');
            return {
                siteCode: rawSite ? JSON.parse(rawSite) : (AppOptions.getSiteCode() || ''),
                configCode: rawConfig ? JSON.parse(rawConfig) : '',
                isCustomConfiguration: rawCustom ? JSON.parse(rawCustom) : false
            };
        }
        catch (_a) {
            return { siteCode: AppOptions.getSiteCode() || '', configCode: '', isCustomConfiguration: false };
        }
    }
    addToDocument(html) {
        var _a;
        (_a = DebugHelper.elById(this.panelElementId)) === null || _a === void 0 ? void 0 : _a.remove();
        const div = document.createElement('div');
        div.innerHTML = html;
        document.body.appendChild(div.firstElementChild);
    }
    setInputValue(id, value) {
        const el = DebugHelper.elById(id);
        if (el)
            el.value = String(value);
    }
    registerEvents() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        // Toggle open/close
        (_b = (_a = DebugHelper.elById(this.panelElementId)) === null || _a === void 0 ? void 0 : _a.querySelector('.debug_details_icon')) === null || _b === void 0 ? void 0 : _b.addEventListener('click', () => {
            const panel = DebugHelper.elById(this.panelElementId);
            panel === null || panel === void 0 ? void 0 : panel.classList.toggle('opened');
        });
        // Clear storage
        (_c = DebugHelper.elById('connextClearAllData')) === null || _c === void 0 ? void 0 : _c.addEventListener('click', (e) => {
            e.preventDefault();
            View.resetStorage();
            localStorageService.delete(StorageKeys.CurrentConversation, { expandKey: false });
            localStorageService.delete(StorageKeys.ConnextTimeRepeatableActions, { expandKey: false });
            localStorageService.delete(StorageKeys.RepeatablesInConv, { expandKey: false });
            localStorageService.delete(StorageKeys.RepeatablesInArticle, { expandKey: false });
            logService.log('[DebugPanel] Storage cleared');
            DebugHelper.reloadApplication();
        });
        // Custom configuration toggle
        (_d = DebugHelper.elById('ConnextCustomConfiguration')) === null || _d === void 0 ? void 0 : _d.addEventListener('change', (e) => {
            const checked = e.target.checked;
            DebugHelper.switchShowDomBlock(checked, DebugHelper.elById('ConnextCustomConfigurationDiv'));
            localStorage.setItem('Connext_Configuration_isCustom', JSON.stringify(checked));
        });
        // Set custom configuration — mirrors ConneXt v2 saveConfiguration()
        (_e = DebugHelper.elById('ConnextSetCustomConfiguration')) === null || _e === void 0 ? void 0 : _e.addEventListener('click', (e) => {
            var _a, _b, _c;
            e.preventDefault();
            const isCustom = (_a = DebugHelper.elById('ConnextCustomConfiguration')) === null || _a === void 0 ? void 0 : _a.checked;
            if (isCustom) {
                const siteCode = ((_b = DebugHelper.elById('ConnextSiteCode')) === null || _b === void 0 ? void 0 : _b.value) || AppOptions.getSiteCode() || '';
                const configCode = (_c = DebugHelper.elById('ConnextConfigCode')) === null || _c === void 0 ? void 0 : _c.value;
                if (siteCode && configCode) {
                    localStorage.setItem('Connext_Configuration_SiteCode', JSON.stringify(siteCode.toUpperCase()));
                    localStorage.setItem('Connext_Configuration_ConfigCode', JSON.stringify(configCode.toUpperCase()));
                    localStorage.setItem('Connext_Configuration_isCustom', JSON.stringify(true));
                }
            }
            else {
                localStorage.removeItem('Connext_Configuration_SiteCode');
                localStorage.removeItem('Connext_Configuration_ConfigCode');
                localStorage.setItem('Connext_Configuration_isCustom', JSON.stringify(false));
            }
            DebugHelper.removePanel();
            window.location.reload();
        });
        // Set conversation views
        (_f = DebugHelper.elById('ConnextCurrentConversationArticleViewsApplyBtn')) === null || _f === void 0 ? void 0 : _f.addEventListener('click', (e) => {
            var _a;
            e.preventDefault();
            const val = parseInt((_a = DebugHelper.elById('ddCurrentConversationArticleViews')) === null || _a === void 0 ? void 0 : _a.value);
            if (!isNaN(val) && val >= 0) {
                View.setCurrentConversationViewCount(val);
                View.saveViewInformation();
                DebugHelper.reloadApplication();
            }
        });
        // Set meter views
        (_g = DebugHelper.elById('ConnextDynamicMeterArticleViewsBtn')) === null || _g === void 0 ? void 0 : _g.addEventListener('click', (e) => {
            var _a;
            e.preventDefault();
            const val = parseInt((_a = DebugHelper.elById('ddDynamicMeterArticleViews')) === null || _a === void 0 ? void 0 : _a.value);
            if (!isNaN(val) && val >= 0) {
                View.setCurrentDynamicMeterViewCount(val);
                View.saveViewInformation();
                DebugHelper.reloadApplication();
            }
        });
        // Set zip codes
        (_h = DebugHelper.elById('actualZipCodes')) === null || _h === void 0 ? void 0 : _h.addEventListener('click', (e) => {
            var _a;
            e.preventDefault();
            const zip = (_a = DebugHelper.elById('actualZipCodesValue')) === null || _a === void 0 ? void 0 : _a.value;
            if (zip) {
                DebugPanelRepository.setZipCodes(zip);
            }
            else {
                DebugPanelRepository.removeZipCodes();
            }
            DebugHelper.reloadApplication();
        });
        // Set engagement rate
        (_j = DebugHelper.elById('ConnextEngagementRateApplyBtn')) === null || _j === void 0 ? void 0 : _j.addEventListener('click', (e) => {
            var _a;
            e.preventDefault();
            const rate = (_a = DebugHelper.elById('ConnextEngagementRate')) === null || _a === void 0 ? void 0 : _a.value;
            DebugPanelRepository.setEngagementRate(rate);
            DebugHelper.reloadApplication();
        });
        // Set custom time
        (_k = DebugHelper.elById('ConnextSetCustomTimeBtn')) === null || _k === void 0 ? void 0 : _k.addEventListener('click', (e) => {
            var _a;
            e.preventDefault();
            const val = (_a = DebugHelper.elById('ConnextCustomTimeTxt')) === null || _a === void 0 ? void 0 : _a.value;
            if (val) {
                customTime.set(val);
            }
            else {
                customTime.delete();
            }
            DebugHelper.reloadApplication();
        });
        // Set custom time initial value
        const timeInput = DebugHelper.elById('ConnextCustomTimeTxt');
        if (timeInput) {
            timeInput.value = customTime.formatDate(customTime.get());
        }
        // Log level change
        (_l = DebugHelper.elById('ConnextLogLevel')) === null || _l === void 0 ? void 0 : _l.addEventListener('change', (e) => {
            DebugPanelRepository.setLogLevel(parseInt(e.target.value));
            DebugHelper.reloadApplication();
        });
        // Debug panel open state toggle
        (_m = DebugHelper.elById('ConnextDebugPanelOpenState')) === null || _m === void 0 ? void 0 : _m.addEventListener('change', (e) => {
            DebugPanelRepository.setDebugPanelOpenState(e.target.checked);
        });
        // Prevent number input scroll changes
        ['ddCurrentConversationArticleViews', 'ddDynamicMeterArticleViews', 'ConnextEngagementRate'].forEach(id => {
            var _a;
            (_a = DebugHelper.elById(id)) === null || _a === void 0 ? void 0 : _a.addEventListener('wheel', (e) => e.preventDefault());
        });
    }
    checkOpenState() {
        var _a;
        const isOpen = DebugPanelRepository.getDebugPanelOpenState();
        const checkbox = DebugHelper.elById('ConnextDebugPanelOpenState');
        if (checkbox)
            checkbox.checked = isOpen;
        if (isOpen) {
            (_a = DebugHelper.elById(this.panelElementId)) === null || _a === void 0 ? void 0 : _a.classList.add('opened');
        }
    }
    /**
     * Apply custom config from debug panel storage (called from AppOptions.init).
     * Kept for backward compat — the actual override now lives in AppOptions.init() directly.
     */
    applyCustomConfig() {
        try {
            const raw = localStorage.getItem('Connext_Configuration_isCustom');
            const isCustom = raw ? JSON.parse(raw) : false;
            if (!isCustom)
                return;
            const rawSite = localStorage.getItem('Connext_Configuration_SiteCode');
            const rawConfig = localStorage.getItem('Connext_Configuration_ConfigCode');
            const siteCode = rawSite ? JSON.parse(rawSite) : null;
            const configCode = rawConfig ? JSON.parse(rawConfig) : null;
            if (siteCode)
                AppOptions.setSiteCode(siteCode);
            if (configCode)
                AppOptions.setConfigCode(configCode);
        }
        catch ( /* no override */_a) { /* no override */ }
    }
    /**
     * Load styles — external plugin.debug.css from resourceUrl + inline fallback.
     */
    loadStyles() {
        if (!document.getElementById('engage-debug-css-link')) {
            const resourceUrl = AppOptions.getResourceUrl();
            if (resourceUrl) {
                const base = resourceUrl.endsWith('/') ? resourceUrl : resourceUrl + '/';
                const cssLink = document.createElement('link');
                cssLink.id = 'engage-debug-css-link';
                document.head.appendChild(cssLink);
                cssLink.rel = 'stylesheet';
                cssLink.type = 'text/css';
                cssLink.href = base + 'dev/css/plugin.debug.css';
            }
        }
        if (document.getElementById('engage-debug-styles'))
            return;
        const style = document.createElement('style');
        style.id = 'engage-debug-styles';
        style.textContent = ``;
        document.head.appendChild(style);
    }
}
export const DebugPanel = new DebugPanelClass();
